import React, { useState, useEffect } from "react";
import { Settings, Shield, Users, Hash, Sun, Moon, Plus, Search, MessageSquare, Globe, X, Users2, UserPlus, User, MoreVertical, ChevronDown, Trash2 } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/components/ui/theme-provider";
import { useUserName } from "@/hooks/use-user-name";
import type { ChatRoomInfo, UserInfo } from "@/lib/types";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@radix-ui/react-dropdown-menu";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import versionData from "../../version.json";
import { PaletteSelector } from "@/components/ui/PaletteSelector";
import { getPaletteById } from "@/lib/palette-config";
import logo from "../../assets/images/Vyzle-logo.svg";

// 👇 UPDATED PROPS INTERFACE
interface ChatSidebarProps {
  user: UserInfo | null;
  chatRooms: ChatRoomInfo[];
  currentRoom: string | null;
  onChatSelect: (roomId: string) => void;
  onCreateChat: () => void;
  onCreateGroup: () => void;
  onOpenSettings: (section?: string) => void;
  isMobile: boolean;
  // ADDED NEW PROPS from ChatPage
  searchQuery: string; // The current search query (controlled by parent)
  isSearching: boolean; // Search visibility (controlled by parent for mobile/desktop sync)
  onToggleSearch: () => void; // Function to open/close search (used for desktop UI)
  onSearchQueryChange: (query: string) => void; // Handler to update the query (used for desktop UI)
}

export function ChatSidebar({
  user,
  chatRooms,
  currentRoom,
  onChatSelect,
  onCreateChat,
  onCreateGroup,
  onOpenSettings,
  isMobile,
  // 👇 ADDED NEW PROPS
  searchQuery,
  isSearching,
  onToggleSearch,
  onSearchQueryChange,
  // 👆 END ADDED NEW PROPS
}: ChatSidebarProps) {
  const { theme, setTheme, palette } = useTheme();
  const activePaletteInfo = getPaletteById(palette);
  const { userName } = useUserName();
  const [displayName, setDisplayName] = useState(userName);
  const [showSearch, setShowSearch] = useState(false);

  // Update display name when userName changes
  useEffect(() => {
    setDisplayName(userName);
  }, [userName]);

  // Listen for localStorage changes to update username immediately
  useEffect(() => {
    const handleStorageChange = () => {
      const newUserName = localStorage.getItem('vyzle-chat-user-name');
      if (newUserName) {
        setDisplayName(newUserName);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    // Also listen for custom event from settings modal
    window.addEventListener('userNameUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userNameUpdated', handleStorageChange);
    };
  }, []);
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const toggleSearch = () => setShowSearch((prev) => !prev);
  const toggleTheme = () => setTheme(theme === "light" ? "dark" : "light");

  let searchQueryText = isMobile ? searchQuery.toLowerCase() : localSearchQuery;
  // const filteredRooms = chatRooms
  //   .filter((room) => room.isActive !== false) // Filter out inactive rooms
  //   .filter((room) => room.name.toLowerCase().includes(searchQueryText.toLowerCase()));

  const filteredRooms = chatRooms
    .filter((room) => room.isActive !== false)
    .filter((room) => room.name.toLowerCase().includes(searchQueryText.toLowerCase()))
    .sort((a, b) => {
      const timeA = a.lastMessage?.timestamp ? new Date(a.lastMessage.timestamp).getTime() : new Date(a?.createdAt || 0).getTime();
      const timeB = b.lastMessage?.timestamp ? new Date(b.lastMessage.timestamp).getTime() : new Date(b?.createdAt || 0).getTime();
      return timeB - timeA;
    });

  const [isFabOpen, setIsFabOpen] = useState(false);
  const toggleFab = () => setIsFabOpen(!isFabOpen);
  const handleOptionClick = (type: string) => {
    console.log(`Option clicked: ${type}`);
    if (type === "group") {
      onCreateGroup();
    } else {
      onCreateChat();
    }
    setIsFabOpen(false); // Close the options after clicking
    // Add your navigation or modal logic here (e.g., navigate to /new-dm)
  };

  return (
    <div
      className={`
    flex flex-col 
    hide-scrollbar
    ${isMobile ? "h-[90dvh] w-full" : "h-[97dvh] w-80"} 
    bg-gray-50 dark:bg-[#0F172A] text-gray-900 dark:text-white 
    pt-6 font-sans 
    border border-[rgba(148,163,184,0.4)] dark:border-[rgba(148,163,184,0.4)]
    // Mobile pe full width hone par rounded corners aur margin hatana behtar hoga
    ${isMobile ? "" : "rounded-xl mr-3"} 
  `}
    >
      {!isMobile && (
        <header className="flex justify-between items-center mb-6 px-4">
          <div className="flex items-center justify-center space-x-3">
            <img src="/Images/logo-light.png" alt="Vyzle" className="h-9 block dark:hidden" />
            <img src="/Images/logo-dark.png" alt="Vyzle" className="h-9 hidden dark:block" />
          </div>
          <div className="flex items-center gap-2.5">
            {/* Search Toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleSearch}
                  className="p-2 rounded-full text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
                  aria-label="Toggle Search"
                >
                  {!isMobile ? (showSearch ? <X size={20} /> : <Search size={20} />) : (isSearching ? <X size={20} /> : <Search size={20} />)}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{showSearch ? "Close search" : "Search"}</p>
              </TooltipContent>
            </Tooltip>

            {/* Settings Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onOpenSettings("profile")}
                  className="p-2 rounded-full text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                  aria-label="Settings"
                >
                  <Settings size={20} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Settings</p>
              </TooltipContent>
            </Tooltip>

            {/* Theme Toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-full text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                  aria-label="Toggle Theme"
                >
                  {(() => {
                    const isSystemDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
                    const effectiveTheme = theme === "system" ? (isSystemDark ? "dark" : "light") : theme;
                    return effectiveTheme === "light" ? <Moon size={20} /> : <Sun size={20} />;
                  })()}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Theme</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </header>
      )}

      {/* User Info / Status Card with Theme Circle */}
      {!isMobile && (
        <div className="space-y-3 mb-3 px-3">
          <div className="flex items-center justify-between w-full p-3 rounded-xl bg-[color-mix(in_srgb,var(--accent)_20%,transparent)] transition-colors">
            <button
              type="button"
              onClick={() => onOpenSettings("profile")}
              className="flex items-center min-w-0 flex-1 text-left cursor-pointer hover:opacity-90 transition-opacity"
              title="Profile settings"
            >
              <div className="relative shrink-0">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 13C20 18 16.5 20.5 12.34 21.95C12.1222 22.0238 11.8855 22.0202 11.67 21.94C7.5 20.5 4 18 4 13V5.99996C4 5.73474 4.10536 5.48039 4.29289 5.29285C4.48043 5.10532 4.73478 4.99996 5 4.99996C7 4.99996 9.5 3.79996 11.24 2.27996C11.4519 2.09896 11.7214 1.99951 12 1.99951C12.2786 1.99951 12.5481 2.09896 12.76 2.27996C14.51 3.80996 17 4.99996 19 4.99996C19.2652 4.99996 19.5196 5.10532 19.7071 5.29285C19.8946 5.48039 20 5.73474 20 5.99996V13Z" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6.37598 18.9102C6.80269 17.7624 7.5704 16.7726 8.576 16.0738C9.5816 15.375 10.777 15.0007 12.0015 15.001C13.2261 15.0013 14.4212 15.3763 15.4265 16.0757C16.4317 16.775 17.1989 17.7652 17.625 18.9132" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12 15C14.2091 15 16 13.2091 16 11C16 8.79086 14.2091 7 12 7C9.79086 7 8 8.79086 8 11C8 13.2091 9.79086 15 12 15Z" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>

              <div className="flex flex-col ml-3 min-w-0 pr-2">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">{displayName || user?.displayName || "Anonymous"}</h2>
                <p className="text-xs text-[var(--accent)] dark:text-[var(--accent)]">Online • Secure</p>
              </div>
            </button>

            {/* Theme circle button opening Appearance settings */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onOpenSettings("appearance")}
                  className="relative w-6 h-6 rounded-full transition-all duration-200 hover:scale-110 ring-2 ring-offset-2 ring-offset-background shrink-0 cursor-pointer shadow-sm"
                  style={{
                    background: `linear-gradient(135deg, ${activePaletteInfo.gradientFrom}, ${activePaletteInfo.gradientTo})`,
                    boxShadow: `0 0 8px ${activePaletteInfo.accent}60`,
                  }}
                  title={`Theme: ${activePaletteInfo.name} (Click to open appearance settings)`}
                  aria-label="Open appearance settings"
                />
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Theme: {activePaletteInfo.name}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      )}
      {/* Search Input */}
      {!isMobile && (<div
        className={`relative px-3 transition-[max-height,opacity] duration-300 overflow-hidden  ${!isMobile ? (showSearch ? "max-h-96 opacity-100 pb-3 pt-2 mb-1" : "max-h-0 opacity-0") : isSearching ? "max-h-96 opacity-100 pb-3 pt-2 mb-1" : "max-h-0 opacity-0"
          }`}
      >
        <Search className="absolute left-3 top-7 -translate-y-1/2 text-gray-500 dark:text-gray-400 w-5 h-5 ml-2" />
        <Input
          placeholder="Search conversations..."
          value={!isMobile ? localSearchQuery : searchQuery}
          onChange={(e) => {
            !isMobile ? setLocalSearchQuery(e.target.value) : onSearchQueryChange(e.target.value);
          }}
          className="
              my-auto
              pl-10
              bg-gray-100 dark:bg-[rgba(148,163,184)]
              border-none
              text-[#94A3B8] dark:bg-[rgba(148,163,184,0.2)]
              focus:border focus:border-[var(--accent)]
          "
        />
      </div>)}
      {/* Action Buttons (Unchanged) */}
      {!isMobile && (
        <div className="flex space-x-2 mb-2 px-3">
          <Button
            onClick={onCreateChat}
            className="
                           w-full flex-1 h-10 rounded-xl text-xs font-semibold text-white transition-all duration-300
                           bg-[var(--bubble-own-from)]
                           shadow-none 
                           hover:shadow-[0_4px_25px_var(--accent-glow)] 
                           active:translate-y-0.5 active:shadow-inner"
          >
            <UserPlus className="w-4 h-4" /> New Chat
          </Button>
          <Button
            onClick={onCreateGroup}
            className="
                           w-full flex-1 h-10 rounded-xl text-xs font-semibold text-white transition-all duration-300
                           bg-[var(--bubble-own-to)] 
                           shadow-none 
                           hover:shadow-[0_4px_25px_var(--accent-glow)] 
                           active:translate-y-0.5 active:shadow-inner"
          >
            <Users className="w-4 h-4 mr-2" />New Group
          </Button>
        </div>
      )}



      {!isMobile && <div className="border-t border-[rgba(148,163,184,0.4)] dark:border-[rgba(148,163,184,0.4)] mb-5" />}

      {/* Chat List */}
      <div className="flex-grow overflow-y-scroll hide-scrollbar px-3 relative min-h-0">
        <h2 className="text-sm text-gray-600 dark:text-gray-400 tracking-wider mb-3">Recent Chats</h2>

        {filteredRooms.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-gray-500 py-10 text-center">
            <MessageSquare className="w-10 h-10 mb-3 opacity-60" />
            <p className="text-sm mb-2">No conversations yet</p>
            <p className="text-xs mb-4">Start by creating one below!</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {filteredRooms.map((room) => {
              const hasUnread = (room.unreadCount || 0) > 0;
              const isSelected = currentRoom === room.roomId;

              return (
                <li key={room.roomId}>
                  {/* Each chat item */}
                  <div
                    className={`group flex items-center justify-between w-full p-3 hover:scale-[1.02] transform duration-200 rounded-lg transition-all ${
                      isSelected
                        ? room.type === "group"
                          ? "bg-[color-mix(in_srgb,var(--bubble-own-to)_20%,transparent)] border-l-8 border-[var(--bubble-own-to)]"
                          : "bg-[color-mix(in_srgb,var(--bubble-own-from)_20%,transparent)] border-l-8 border-[var(--bubble-own-from)]"
                        : room.type === "group"
                          ? "border-l-8 border-[var(--bubble-own-to)] bg-transparent hover:bg-black/5 dark:hover:bg-white/5"
                          : "border-l-8 border-[var(--bubble-own-from)] bg-transparent hover:bg-black/5 dark:hover:bg-white/5"
                    }`}
                  >
                    {/* Left side — Avatar + Info */}
                    <button onClick={() => onChatSelect(room.roomId)} className="flex flex-1 items-center text-left gap-3 min-w-0">
                      <Avatar
                        className={`w-10 h-10 mr-1 shrink-0 overflow-visible ${room.type === "group"
                          ? "shadow-[4px_4px_0px_0px_var(--bubble-own-to)]"
                          : "shadow-[4px_4px_0px_0px_var(--bubble-own-from)]"
                          }`}
                      >
                        <AvatarFallback className={`bg-[#f8fafc]/20 rounded-full backdrop-blur-[12px] ${room.type === "group"
                          ? "shadow-[4px_4px_0px_0px_var(--bubble-own-to)]"
                          : "shadow-[4px_4px_0px_0px_var(--bubble-own-from)]"
                          } flex items-center justify-center border border-[rgba(248,250,252,0.2)]`} >{room.type === "group" ? <Users className="w-4 h-4" /> : <User className="w-4 h-4" />}</AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0 pr-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`truncate flex-1 min-w-0 ${hasUnread ? "font-bold text-gray-950 dark:text-white" : "font-medium text-gray-900 dark:text-white"}`}>
                            {room?.name}
                          </p>
                          {hasUnread && (
                            <div className="flex items-center justify-center shrink-0 ml-1.5">
                              <span className="relative flex h-2.5 w-2.5">
                                <span
                                  className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                                    room.type === "group" ? "bg-[var(--bubble-own-to)]" : "bg-[var(--bubble-own-from)]"
                                  }`}
                                />
                                <span
                                  className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                                    room.type === "group"
                                      ? "bg-[var(--bubble-own-to)] shadow-[0_0_8px_var(--bubble-own-to)]"
                                      : "bg-[var(--bubble-own-from)] shadow-[0_0_8px_var(--bubble-own-from)]"
                                  }`}
                                />
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-0.5">
                          <p className={`text-xs truncate flex-1 min-w-0 ${hasUnread ? "font-semibold text-gray-900 dark:text-gray-200" : "text-gray-600 dark:text-gray-400"}`}>
                            {room.lastMessage ? room.lastMessage.content : "No messages yet"}
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {isMobile && (
          <div className="fixed bottom-20 right-4 z-50 flex flex-col items-end">
            {/* FAB OPTIONS (ABOVE THE + BUTTON) */}
            {isFabOpen && (
              <div className="flex flex-col items-end space-y-3 mb-4">
                {/* Option 1 */}
                <button
                  onClick={() => handleOptionClick("group")}
                  className="
            p-3 rounded-full text-white shadow-md
            transition-all duration-300
            focus:outline-none focus:ring-4 focus:ring-[var(--bubble-own-to)]/50
            bg-[var(--bubble-own-to)]
          "
                  aria-label="New Group Chat"
                >
                  <Users2 className="w-5 h-5" />
                </button>

                {/* Option 2 */}
                <button
                  onClick={() => handleOptionClick("dm")}
                  className="
            p-3 rounded-full text-white shadow-md
            transition-all duration-300
            focus:outline-none focus:ring-4 focus:ring-[var(--bubble-own-from)]/50
            bg-[var(--bubble-own-from)]
          "
                  aria-label="New Direct Message"
                >
                  <UserPlus className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* MAIN FAB (+ BUTTON) */}
            <button
              onClick={toggleFab}
              className={`
        p-4 rounded-full text-white shadow-xl
        transition-all duration-300 ease-in-out
        ${isFabOpen ? "bg-red-500 hover:bg-red-600 rotate-45" : "bg-[var(--accent)]"}
      `}
              aria-label={isFabOpen ? "Close Options" : "Open Options"}
            >
              <Plus className="w-6 h-6" />
            </button>
          </div>
        )}
      </div>

      {!isMobile && (
        <footer className="mt-auto px-3 py-3 border-t border-[rgba(148,163,184,0.4)] dark:border-[rgba(148,163,184,0.4)]">
          <p className="text-[10px] text-center text-gray-500 dark:text-gray-400">
            Your account will be automatically deactivated after 30 days of inactivity.
          </p>
          <div className="text-center text-[10px] text-gray-400 dark:text-gray-600 mt-1">
            v{versionData.version} ({versionData.build})
          </div>
        </footer>
      )}
      {isMobile && (
        <footer className=" px-3  border-t border-[rgba(148,163,184,0.4)] dark:border-[rgba(148,163,184,0.4)] ">

          {/* <div className="flex items-center justify-center mt-3">
            <PaletteSelector />
          </div> */}
          <p className="text-[10px] mt-1 text-center text-gray-500 dark:text-gray-400">
            Your account will be automatically deactivated after 30 days of inactivity.
          </p>
          <div className="text-center text-[8px] text-gray-400 dark:text-gray-600 mt-1 mb-1">
            v{versionData.version} ({versionData.build})
          </div>
        </footer>
      )}
    </div>
  );
}